/**
 * 클래스 — 학원 모드. 수업 시간 묶음 + 학생 배정 단위.
 * 클래스 목록 + 생성 + 사용 기능 토글(과제·성적·공지·자료실).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoAdd, IoClose, IoChevronForward, IoOptionsOutline, IoInformationCircle,
} from 'react-icons/io5';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';
import Badge from '../../components/common/Badge';

const DAYS = ['월', '화', '수', '목', '금', '토', '일'];
const COLORS = ['#4F46E5', '#22C55E', '#F59E0B', '#EF4444', '#06B6D4'];
const DEFAULT_FEATURES = {
  attendance: true, assignment: true, grade: true, notice: true, material: true, log: true,
};
const FEATURE_LIST = [
  { key: 'attendance', label: '출석', fixed: true },
  { key: 'assignment', label: '과제' },
  { key: 'grade', label: '성적' },
  { key: 'notice', label: '공지' },
  { key: 'material', label: '자료실' },
  { key: 'log', label: '수업일지' },
];

const BizClassesPage = () => {
  const navigate = useNavigate();
  const { operatedAcademy } = useAcademy();
  const aid = operatedAcademy?.id;

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', color: COLORS[0] });
  const [schedule, setSchedule] = useState([]);
  const [day, setDay] = useState('월');
  const [time, setTime] = useState('');
  const [feat, setFeat] = useState(DEFAULT_FEATURES);

  const load = useCallback(async () => {
    if (!aid) { setLoading(false); return; }
    const [cls, aca] = await Promise.all([
      academyService.getClasses(aid),
      academyService.getAcademy(aid),
    ]);
    setClasses(cls);
    setFeat({ ...DEFAULT_FEATURES, ...(aca?.features || {}) });
    setLoading(false);
  }, [aid]);

  useEffect(() => { load(); }, [load]);

  const openModal = () => {
    setForm({ name: '', color: COLORS[0] });
    setSchedule([]);
    setDay('월');
    setTime('');
    setModal(true);
  };

  const addSlot = () => {
    const t = time.trim();
    if (!t) return;
    setSchedule((s) => [...s, { day, time: t }]);
    setTime('');
  };

  const save = async () => {
    if (!form.name.trim() || !aid) return;
    setSaving(true);
    try {
      await academyService.createClass(aid, { ...form, name: form.name.trim(), schedule });
      setModal(false);
      await load();
    } catch (e) {
      console.error('수업 생성 실패:', e);
      alert('수업 생성에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = async (key) => {
    const prev = feat;
    const next = { ...feat, [key]: !feat[key] };
    setFeat(next);
    try {
      await academyService.updateAcademy(aid, { features: next });
    } catch (e) {
      console.error('기능 설정 저장 실패:', e);
      setFeat(prev);
    }
  };

  if (!operatedAcademy) {
    return (
      <Container>
        <Empty>
          <EmptyText>운영 중인 학원이 없어요.</EmptyText>
          <EmptyBtn type="button" onClick={() => navigate('/academy/register')}>학원 신청하기</EmptyBtn>
        </Empty>
      </Container>
    );
  }
  if (loading) return <BrandLoader />;

  return (
    <Container>
      <Head>
        <div>
          <Title>수업</Title>
          <Sub>{operatedAcademy.name} · {classes.length}개</Sub>
        </div>
        <NewBtn type="button" onClick={openModal}><IoAdd size={18} /> 수업</NewBtn>
      </Head>

      <FeatSection>
        <FeatHead>
          <IoOptionsOutline size={17} color="#4F46E5" />
          <FeatTitle>수업 기능 설정</FeatTitle>
          <FeatBadge>핵심</FeatBadge>
        </FeatHead>
        <Bubble>
          <BubbleIcon><IoInformationCircle size={17} color="#4F46E5" /></BubbleIcon>
          <BubbleText>
            켠 기능은 <Em>학부모님 앱에 그대로</Em> 보여요. 출석·성적·과제를
            꼼꼼히 기록할수록 <Em>“관리 잘하는 학원”</Em>으로 믿음을 얻어요.
            안 쓰는 기능만 꺼두세요.
          </BubbleText>
        </Bubble>
        <FeatChips>
          {FEATURE_LIST.map((f) => {
            const on = f.fixed || !!feat[f.key];
            return (
              <FeatChip
                key={f.key}
                type="button"
                $on={on}
                onClick={() => { if (!f.fixed) toggleFeature(f.key); }}
              >
                {f.label}
                <ChipState $on={on}>{f.fixed ? '필수' : (feat[f.key] ? '사용' : '끔')}</ChipState>
              </FeatChip>
            );
          })}
        </FeatChips>
      </FeatSection>

      {classes.length === 0 ? (
        <Empty>
          <EmptyText>아직 수업이 없어요.</EmptyText>
          <EmptySub>원하는 시간을 묶어 수업을 만들고 학생을 배정하세요.</EmptySub>
        </Empty>
      ) : (
        classes.map((c) => (
          <ClassCard key={c.id} type="button" onClick={() => navigate(`/app/biz/classes/${c.id}`)}>
            <CardBody>
              <CardTop>
                <ColorDot $color={c.color || '#4F46E5'} />
                <CName $color={c.color || '#4F46E5'}>{c.name}</CName>
              </CardTop>
              {c.schedule?.length > 0 && (
                <Slots>
                  {c.schedule.map((s, i) => <Badge key={i} $size="sm">{s.day} {s.time}</Badge>)}
                </Slots>
              )}
              <CardFoot>학생 {c.students || c.studentIds?.length || 0}명</CardFoot>
            </CardBody>
            <IoChevronForward size={18} color="#C2C5CE" />
          </ClassCard>
        ))
      )}

      {modal && (
        <Overlay onClick={() => setModal(false)}>
          <Sheet onClick={(e) => e.stopPropagation()}>
            <SheetTitle>수업 만들기</SheetTitle>

            <FLabel>수업 이름</FLabel>
            <FInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus />

            <FLabel>수업 시간</FLabel>
            <DayRow>
              {DAYS.map((d) => (
                <DayBtn key={d} type="button" $on={day === d} onClick={() => setDay(d)}>{d}</DayBtn>
              ))}
            </DayRow>
            <SlotAdd>
              <FInput
                value={time}
                onChange={(e) => setTime(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSlot(); } }}
                style={{ flex: 1 }}
              />
              <AddSlotBtn type="button" onClick={addSlot}><IoAdd size={18} /></AddSlotBtn>
            </SlotAdd>
            <SlotHint>요일 선택 후 시간 입력 · 예: 13:00~14:00</SlotHint>
            {schedule.length > 0 && (
              <Slots>
                {schedule.map((s, i) => (
                  <SlotChip key={i} type="button" onClick={() => setSchedule(schedule.filter((_, idx) => idx !== i))}>
                    {s.day} {s.time} <IoClose size={12} />
                  </SlotChip>
                ))}
              </Slots>
            )}

            <FLabel>색상</FLabel>
            <ColorRow>
              {COLORS.map((c) => (
                <ColorBtn key={c} type="button" $color={c} $on={form.color === c} onClick={() => setForm({ ...form, color: c })} />
              ))}
            </ColorRow>

            <SheetActions>
              <CancelBtn type="button" onClick={() => setModal(false)}>취소</CancelBtn>
              <SaveBtn type="button" disabled={!form.name.trim() || saving} onClick={save}>
                {saving ? '저장 중...' : '만들기'}
              </SaveBtn>
            </SheetActions>
          </Sheet>
        </Overlay>
      )}
    </Container>
  );
};

export default BizClassesPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 22px 16px;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 4px;
`;

const NewBtn = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 9px 14px;
  border-radius: 11px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
`;

const FeatSection = styled.div`
  margin-bottom: 18px;
  padding: 14px;
  border-radius: 14px;
  background: #F0F4FF;
  border: 1px solid #D9E2FF;
`;

const FeatHead = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
`;

const FeatTitle = styled.p`
  font-size: 14.5px;
  font-weight: 800;
  color: #1E3A5F;
`;

const FeatBadge = styled.span`
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 10.5px;
  font-weight: 800;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
`;

const Bubble = styled.div`
  position: relative;
  display: flex;
  gap: 8px;
  background: #fff;
  border: 1px solid #D9E2FF;
  border-radius: 12px;
  padding: 11px 12px;
  margin-bottom: 12px;

  /* 위쪽을 향한 말풍선 꼬리 — 제목에서 이어지는 느낌 */
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 18px;
    border-style: solid;
  }
  &::before {
    top: -9px;
    border-width: 0 9px 9px 9px;
    border-color: transparent transparent #D9E2FF transparent;
  }
  &::after {
    top: -8px;
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent #fff transparent;
  }
`;

const BubbleIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  padding-top: 1px;
`;

const BubbleText = styled.p`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.65;
  color: #374151;
`;

const Em = styled.span`
  font-weight: 800;
  color: #4F46E5;
`;

const FeatChips = styled.div`
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
`;

const FeatChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 13px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  background: #fff;
  border: 1px solid ${({ $on }) => ($on ? '#C7D3F5' : '#E5E7EB')};
  color: ${({ $on, theme }) => ($on ? theme.colors.text.primary : theme.colors.text.hint)};
`;

const ChipState = styled.span`
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#C2C5CE')};
`;

const ClassCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px;
  margin-bottom: 10px;
  border-radius: 14px;
  background: #F4F5F7;
  text-align: left;
`;

const ColorDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const CardBody = styled.div`
  flex: 1;
  min-width: 0;
  padding: 15px 0;
`;

const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  gap: 7px;
`;

const CName = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: ${({ $color, theme }) => $color || theme.colors.text.primary};
`;

const Slots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
`;

const CardFoot = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 9px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 90px 0;
`;

const EmptyText = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptySub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  margin-top: 6px;
  padding: 12px 26px;
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 88vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 22px 18px 28px;
`;

const SheetTitle = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 8px;
`;

const FLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 16px 0 7px;
`;

const FInput = styled.input`
  width: 100%;
  padding: 13px 14px;
  border: 0;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 15px;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const DayRow = styled.div`
  display: flex;
  gap: 5px;
`;

const DayBtn = styled.button`
  flex: 1;
  padding: 9px 0;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#F4F5F7')};
  color: ${({ $on, theme }) => ($on ? '#fff' : theme.colors.text.secondary)};
`;

const SlotAdd = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 7px;
`;

const AddSlotBtn = styled.button`
  flex-shrink: 0;
  width: 48px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SlotHint = styled.p`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 6px;
`;

const SlotChip = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 700;
`;

const ColorRow = styled.div`
  display: flex;
  gap: 9px;
  flex-wrap: wrap;
`;

const ColorBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 3px solid ${({ $on, $color }) => ($on ? $color : 'transparent')};
  outline: ${({ $on }) => ($on ? '2px solid #E5E7EB' : 'none')};
  outline-offset: 1px;
`;

const SheetActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 24px;
`;

const CancelBtn = styled.button`
  flex: 1;
  padding: 14px;
  border-radius: 12px;
  background: #F4F5F7;
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SaveBtn = styled.button`
  flex: 2;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;
